import React, { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquarePlus, Star, CheckCircle } from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import { toast } from 'sonner';

const BACKEND_URL = '';

function StarRating({ rating, setRating }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(star => (
        <button key={star} type="button" data-testid={`star-${star}`}
          onClick={() => setRating(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="p-0.5 transition-transform hover:scale-110">
          <Star className={`h-8 w-8 transition-colors ${
            star <= (hover || rating)
              ? 'fill-amber-400 text-amber-400'
              : 'fill-none text-slate-300'
          }`} />
        </button>
      ))}
      {rating > 0 && (
        <span className="ml-2 text-sm text-slate-500 self-center">
          {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][rating]}
        </span>
      )}
    </div>
  );
}

export default function FeedbackPage({ user }) {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [history, setHistory] = useState([]);

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/feedback/modules`, { credentials: 'include', headers })
      .then(r => r.json()).then(d => setModules(d.modules || [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/feedback`, { credentials: 'include', headers })
      .then(r => r.json()).then(d => setHistory(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!selectedModule) { toast.error('Please select a module'); return; }
    if (!rating) { toast.error('Please give a rating'); return; }
    setSubmitting(true);
    const mod = modules.find(m => m.id === selectedModule);
    try {
      const res = await fetch(`${BACKEND_URL}/api/feedback`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify({
          module_id: selectedModule,
          module_label: mod?.label || selectedModule,
          rating,
          comment,
        }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      setSubmitted(true);
      // Add to local history
      setHistory(prev => [{
        module_label: mod?.label, rating, comment,
        created_at: new Date().toISOString(), user_name: user?.name,
      }, ...prev]);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setSelectedModule(''); setRating(0); setComment(''); setSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="app" type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold font-heading mb-2">
              <span className="gradient-text">Feedback</span>
            </h1>
            <p className="text-slate-600">Help us improve your experience. Rate any module and share your thoughts.</p>
          </div>

          {/* Feedback Form */}
          <Card className="p-6 border border-slate-200 mb-8" data-testid="feedback-form">
            {submitted ? (
              <div className="text-center py-8" data-testid="feedback-success">
                <div className="w-16 h-16 bg-brand-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-brand-sage" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">Thank you for your feedback!</h3>
                <p className="text-sm text-slate-500 mb-6">Your input helps us shape a better platform for the community.</p>
                <Button data-testid="submit-another-btn" onClick={resetForm}
                  className="bg-gradient-to-r from-brand-navy to-brand-sage text-white">
                  Submit Another
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Module selector */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Which module are you rating?</label>
                  <Select value={selectedModule} onValueChange={setSelectedModule}>
                    <SelectTrigger data-testid="module-select" className="border-slate-200">
                      <SelectValue placeholder="Select a module or BriteLab..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {modules.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Star rating */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Your rating</label>
                  <StarRating rating={rating} setRating={setRating} />
                </div>

                {/* Comment */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Your feedback <span className="text-slate-400 font-normal">(optional)</span></label>
                  <Textarea data-testid="feedback-comment" placeholder="What did you like? What could be better?"
                    value={comment} onChange={e => setComment(e.target.value)}
                    className="min-h-28 border-slate-200 resize-none" />
                </div>

                <Button data-testid="submit-feedback-btn" onClick={handleSubmit} disabled={submitting || !selectedModule || !rating}
                  className="w-full bg-gradient-to-r from-brand-navy to-brand-sage hover:from-brand-dark hover:to-brand-sage text-white h-11">
                  {submitting ? 'Submitting...' : 'Submit Feedback'}
                </Button>
              </div>
            )}
          </Card>

          {/* Previous feedback */}
          {history.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3">
                {user?.role === 'admin' ? 'All Feedback' : 'Your Previous Feedback'}
              </h3>
              <div className="space-y-3">
                {history.map((fb, i) => (
                  <Card key={i} className="p-4 border border-slate-200" data-testid={`feedback-item-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-medium text-slate-800">{fb.module_label}</span>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map(s => (
                              <Star key={s} className={`h-3.5 w-3.5 ${s <= fb.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                            ))}
                          </div>
                        </div>
                        {fb.comment && <p className="text-sm text-slate-500 mt-1">{fb.comment}</p>}
                        <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                          {user?.role === 'admin' && fb.user_name && <span>{fb.user_name}</span>}
                          <span>{new Date(fb.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
            </div>
            <PageSideRail placement="app" />
          </div>
        </div>
      </main>
    </div>
  );
}
